import React, { Component } from 'react'
import { GridToolbarColumnsButton, GridToolbarDensitySelector } from '@mui/x-data-grid'
import { GridToolbarFilterButton, GridToolbarQuickFilter } from '@mui/x-data-grid'
import { Box, BoxProps, GlobalStyles, styled, Typography } from '@mui/material'
import { BtnFormCreate } from './ui.buttons'
import { Breadcrumbs, IBreadcrumbConfig, HelpTooltip } from '../components'
import { IExportCsvOptionsModel } from './csv-export-helper'
import { CustomExportButton } from './custom.export-button'

export interface IToolbarPannelToolbarConfig {
  columns?: boolean
  filters?: boolean
  density?: boolean
  export?: boolean
}

export interface IToolbarPannelProps {
  title?: React.ReactNode
  titleProps?: BoxProps
  icon?: React.ReactNode
  afterTitle?: React.ReactNode
  searchInclude?: string[]
  visibilityToolbar?: IToolbarPannelToolbarConfig
  formCreate?: React.ReactNode
  breadcrumbs?: IBreadcrumbConfig[]
  afterAction?: React.ReactNode
  belowAction?: React.ReactNode
  aboveAction?: React.ReactNode
  actionWrapProps?: BoxProps
  sx?: BoxProps['sx']
  exportCsvOptions?: IExportCsvOptionsModel
}

class ToolbarPannel extends Component<React.PropsWithChildren<IToolbarPannelProps>> {
  private _popperObserver: MutationObserver | null = null
  private _elementObserver: MutationObserver | null = null
  private _observedPopper: HTMLElement | null = null

  componentDidMount() {
    // observe DOM for filter popper being added; when found, observe + fix transform on mobile
    this._popperObserver = new MutationObserver(() => {
      // selector based on class visible in DevTools (no MuiPopper-root prefix)
      const popper = document.querySelector('.MuiDataGrid-panel') as HTMLElement | null
      if (!popper) return
      if (!window.matchMedia('(max-width:768px)').matches) return
      if (this._observedPopper === popper) return

      this._observedPopper = popper

      const applyFix = () => {
        // extract Y from current transform, keep it; force X = 8px
        const raw = popper.style.transform || ''
        const m = raw.match(/translate3d\([^,]+,\s*([^,]+),\s*[^\)]+\)/)
        const y = m ? m[1].trim() : '0px'
        popper.style.transform = `translate3d(8px, ${y}, 0px)`
      }

      applyFix()

      // re-apply every time Popper writes a new inline transform
      if (this._elementObserver) this._elementObserver.disconnect()
      this._elementObserver = new MutationObserver((muts) => {
        for (const mut of muts) {
          if (mut.attributeName === 'style') {
            // guard: avoid infinite loop — only re-apply if X is not already 8px
            const cur = popper.style.transform || ''
            if (!cur.startsWith('translate3d(8px')) applyFix()
          }
        }
      })
      this._elementObserver.observe(popper, { attributes: true, attributeFilter: ['style'] })
    })

    this._popperObserver.observe(document.body, { childList: true, subtree: true })
  }

  componentWillUnmount() {
    if (this._popperObserver) {
      this._popperObserver.disconnect()
      this._popperObserver = null
    }
    if (this._elementObserver) {
      this._elementObserver.disconnect()
      this._elementObserver = null
    }
    this._observedPopper = null
  }

  render() {
    return (
      <>
        <GlobalStyles
          styles={{
            '@media (max-width:768px)': {
              '.MuiDataGrid-panel': {
                left: '8px !important',
                overflow: 'hidden',
                maxWidth: 'calc(100svw - 16px)',
                width: '100%',
                '.MuiPaper-root, .MuiDataGrid-paper': { margin: '0 8px' },
                '.MuiDataGrid-panelContent': {
                  overflow: 'auto',
                  '.MuiFormControl-root': { width: 'auto' },
                  '.MuiDataGrid-filterFormValueInput': { width: '100px' }
                }
              }
            }
          }}
        />
        <ToolbarPannelStyled className={toolbarPannelClasses.root} sx={this.props.sx}>
          {this.renderTitle()}
          {this.props.aboveAction}
          <Box className={toolbarPannelClasses.actionsRow} {...this.props.actionWrapProps}>
            <Box className={toolbarPannelClasses.actionsLeft}>
              <Box className={toolbarPannelClasses.gridToolbar}>
                {this.props.visibilityToolbar?.columns !== false && <GridToolbarColumnsButton />}
                {this.props.visibilityToolbar?.filters !== false && <GridToolbarFilterButton />}
                {this.props.visibilityToolbar?.density !== false && <GridToolbarDensitySelector />}
                {this.props.visibilityToolbar?.export !== false && <CustomExportButton csvOptions={this.props.exportCsvOptions} />}
              </Box>
              {this.props.formCreate && <BtnFormCreate>{this.props.formCreate}</BtnFormCreate>}
              {this.props.afterAction}
            </Box>
            <Box className={toolbarPannelClasses.actionsRight}>
              <Box className={toolbarPannelClasses.quickFilterWrap}>
                <GridToolbarQuickFilter fullWidth variant='standard' size='small' debounceMs={800} quickFilterParser={(x: any) => [x]} />
                {this.renderEndAdornment()}
              </Box>
            </Box>
          </Box>
          {this.props.belowAction}
        </ToolbarPannelStyled>
      </>
    )
  }

  renderTitle = () => {
    const { title, afterTitle, breadcrumbs, icon } = this.props
    const titleClassName = `${toolbarPannelClasses.titleWrap}${this.props.titleProps?.className ? ` ${this.props.titleProps.className}` : ''}`

    const isVisible = !!title || !!breadcrumbs || !!afterTitle
    if (!isVisible) return <></>

    let titleElm = title
    if (typeof title === 'string') {
      titleElm = (
        <Typography noWrap variant='subtitle1' className={toolbarPannelClasses.titleText}>
          {title}
        </Typography>
      )
    }
    if (breadcrumbs) titleElm = <Breadcrumbs value={breadcrumbs} />

    return (
      <Box {...this.props.titleProps} className={titleClassName}>
        {icon && <div className={toolbarPannelClasses.icon}>{icon}</div>}
        {titleElm}
        {afterTitle}
      </Box>
    )
  }

  renderEndAdornment = () => {
    if (!this.props.searchInclude || this.props.searchInclude.length <= 0) return <></>
    return (
      <HelpTooltip title='The search includes' small>
        <ul className={toolbarPannelClasses.searchHelp}>
          {this.props.searchInclude.map((item, index) => (
            <li key={index}>
              <Typography variant='body2'>{item}</Typography>
            </li>
          ))}
        </ul>
      </HelpTooltip>
    )
  }
}
export default ToolbarPannel

const toolbarPannelClasses = {
  root: 'Dino-ToolbarPannel-root',
  actionsRow: 'Dino-ToolbarPannel-actionsRow',
  actionsLeft: 'Dino-ToolbarPannel-actionsLeft',
  actionsRight: 'Dino-ToolbarPannel-actionsRight',
  quickFilterWrap: 'Dino-ToolbarPannel-quickFilterWrap',
  gridToolbar: 'Dino-ToolbarPannel-gridToolbar',
  titleWrap: 'Dino-ToolbarPannel-titleWrap',
  titleText: 'Dino-ToolbarPannel-titleText',
  searchHelp: 'Dino-ToolbarPannel-searchHelp',
  icon: 'Dino-ToolbarPannel-icon'
}

const ToolbarPannelStyled = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2, 0),
  [`.${toolbarPannelClasses.actionsRow}`]: { display: 'flex', flexWrap: 'wrap' },
  [`.${toolbarPannelClasses.actionsLeft}`]: { flex: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' },
  [`.${toolbarPannelClasses.actionsRight}`]: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    width: 'auto',
    [theme.breakpoints.down('md')]: { width: '100%' }
  },
  [`.${toolbarPannelClasses.quickFilterWrap}`]: { display: 'flex', alignItems: 'center' },
  [`.${toolbarPannelClasses.gridToolbar}`]: { padding: 0 },
  [`.${toolbarPannelClasses.titleWrap}`]: { height: 'var(--height-table-topbar, 48px)', display: 'flex', alignItems: 'center' },
  [`.${toolbarPannelClasses.titleText}`]: { fontWeight: 700, flex: 1 },
  [`.${toolbarPannelClasses.icon}`]: {
    marginRight: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto'
  },
  [`.${toolbarPannelClasses.searchHelp}`]: {
    margin: '0 0 0 18px',
    padding: 0,
    li: { position: 'relative' },
    'li::after': {
      content: '"►"',
      display: 'inline-block',
      top: '50%',
      transform: 'translateY(-50%)',
      position: 'absolute',
      left: '-18px'
    }
  },
  [theme.breakpoints.down('md')]: { padding: theme.spacing(1, 1, 0) }
}))
